import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { useMenu } from '../../../shared/hooks/useMenu.js';
import { useTranslation } from '../../../shared/i18n/index.js';
import '../../../shared/tokens/brand-dashboard.css';

export function DashboardPage(): ReactNode {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const { menu } = useMenu('/api', token, 'web');
  const who = user?.displayName || user?.username || 'user';

  // The panels describe the whole product; the link is only offered to someone
  // whose menu already grants the page, so a panel never leads to a refusal.
  const canOpen = (path: string): boolean =>
    (menu?.items ?? []).some((i) => i.isVisible && i.path === path);

  const openLink = (path: string): ReactNode =>
    canOpen(path) ? <Link to={path}>{t('dashboardPage.panel.open')} →</Link> : null;

  return (
    <main aria-label="dashboard" className="bo-page">
      <section className="bo-welcome">
        <div>
          <p className="bo-kicker">{t('dashboardPage.kicker')}</p>
          <h1>{t('dashboardPage.title')}</h1>
          <p className="bo-welcome__sub">{t('dashboardPage.welcome', { name: who })}</p>
        </div>
      </section>

      <section className="bo-metrics" aria-label={t('dashboardPage.capabilities')}>
        <article className="bo-card">
          <header>
            <span className="bo-card__icon" aria-hidden="true">₹</span>
            <span className="bo-card__state">{t('dashboardPage.cap.financialControl')}</span>
          </header>
          <p className="bo-card__label">{t('dashboardPage.cap.budgetPosition')}</p>
          <strong className="bo-card__value">{t('dashboardPage.cap.planAndTrack')}</strong>
          <small className="bo-card__hint">{t('dashboardPage.cap.budgetHint')}</small>
          <div className="bo-rule" aria-hidden="true"><span /><span /><span /></div>
        </article>

        <article className="bo-card">
          <header>
            <span className="bo-card__icon bo-card__icon--gold" aria-hidden="true">✓</span>
            <span className="bo-card__state">{t('dashboardPage.cap.executionControl')}</span>
          </header>
          <p className="bo-card__label">{t('dashboardPage.cap.activityProgress')}</p>
          <strong className="bo-card__value">{t('dashboardPage.cap.stayOnTrack')}</strong>
          <small className="bo-card__hint">{t('dashboardPage.cap.activityHint')}</small>
          <div className="bo-rule bo-rule--gold" aria-hidden="true"><span /><span /><span /></div>
        </article>

        <article className="bo-card">
          <header>
            <span className="bo-card__icon bo-card__icon--coral" aria-hidden="true">⌖</span>
            <span className="bo-card__state">{t('dashboardPage.cap.marketVisibility')}</span>
          </header>
          <p className="bo-card__label">{t('dashboardPage.cap.brandReach')}</p>
          <strong className="bo-card__value">{t('dashboardPage.cap.knowYourReach')}</strong>
          <small className="bo-card__hint">{t('dashboardPage.cap.reachHint')}</small>
          <div className="bo-dots" aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
        </article>

        <article className="bo-card">
          <header>
            <span className="bo-card__icon bo-card__icon--ink" aria-hidden="true">◎</span>
            <span className="bo-card__state">{t('dashboardPage.cap.governance')}</span>
          </header>
          <p className="bo-card__label">{t('dashboardPage.cap.auditVisibility')}</p>
          <strong className="bo-card__value">{t('dashboardPage.cap.remainTransparent')}</strong>
          <small className="bo-card__hint">{t('dashboardPage.cap.auditHint')}</small>
          <div className="bo-rule" aria-hidden="true"><span /><span /><span /></div>
        </article>
      </section>

      <section className="bo-grid">
        <article className="bo-panel">
          <header className="bo-panel__header">
            <div>
              <p className="bo-kicker">{t('dashboardPage.panel.budgetKicker')}</p>
              <h2>{t('dashboardPage.panel.budgetTitle')}</h2>
            </div>
            {openLink('/sh/budget')}
          </header>
          <div className="bo-empty bo-empty--tall">            <span className="bo-empty__icon" aria-hidden="true">₹</span>
            <div>
              <strong>{t('dashboardPage.panel.budgetEmptyTitle')}</strong>
              <p>{t('dashboardPage.panel.budgetEmptyBody')}</p>
            </div>
          </div>
        </article>

        <article className="bo-panel">
          <header className="bo-panel__header">
            <div>
              <p className="bo-kicker">{t('dashboardPage.panel.approvalsKicker')}</p>
              <h2>{t('dashboardPage.panel.approvalsTitle')}</h2>
            </div>
            {openLink('/inbox')}
          </header>
          <div className="bo-empty bo-empty--short">
            <span className="bo-empty__icon" aria-hidden="true">◷</span>
            <div>
              <strong>{t('dashboardPage.panel.approvalsEmptyTitle')}</strong>
              <p>{t('dashboardPage.panel.approvalsEmptyBody')}</p>
            </div>
          </div>
        </article>

        <article className="bo-panel">
          <header className="bo-panel__header">
            <div>
              <p className="bo-kicker">{t('dashboardPage.panel.executionKicker')}</p>
              <h2>{t('dashboardPage.panel.executionTitle')}</h2>
            </div>
            {openLink('/jobs/journey')}
          </header>
          <div className="bo-empty">
            <span className="bo-empty__icon" aria-hidden="true">✓</span>
            <div>
              <strong>{t('dashboardPage.panel.executionEmptyTitle')}</strong>
              <p>{t('dashboardPage.panel.executionEmptyBody')}</p>
            </div>
          </div>
        </article>

        <article className="bo-panel">
          <header className="bo-panel__header">
            <div>
              <p className="bo-kicker">{t('dashboardPage.panel.coverageKicker')}</p>
              <h2>{t('dashboardPage.panel.coverageTitle')}</h2>
            </div>
            {openLink('/admin/branding-tracking')}
          </header>
          <div className="bo-empty bo-empty--short">
            <span className="bo-empty__icon" aria-hidden="true">⌖</span>
            <div>
              <strong>{t('dashboardPage.panel.coverageEmptyTitle')}</strong>
              <p>{t('dashboardPage.panel.coverageEmptyBody')}</p>
            </div>
          </div>
        </article>
      </section>

      <footer className="bo-footer">
        <span><i aria-hidden="true" />{t('dashboardPage.footerLeft')}</span>
        <span>{t('dashboardPage.footerRight')}</span>
      </footer>
    </main>
  );
}
